import { Stack } from '@mui/material';
import type { KeyboardEvent, MutableRefObject } from 'react';
import { Macro, RecordFolder } from 'src/graphql';
import { GroupedFolders } from 'src/pages/Conversations/Chat/Macros/macros-modal/types';
import { MacroCreateFolderRow } from './macro-create-folder-row';
import { MacroEmptyState } from './macro-empty-state';
import { MacroFolderList } from './macro-folder-list';
import { MacroLoadingState } from './macro-loading-state';

interface MacroListContentProps {
  isCreatingFolder: boolean;
  newFolderName: string;
  onNewFolderNameChange: (value: string) => void;
  onCreateFolder: () => void;
  onCancelCreateFolder: () => void;
  createFolderLoading: boolean;
  isLoading: boolean;
  isSearching: boolean;
  hasResults: boolean;
  groupedFolders: GroupedFolders;
  expandedFolderIds: Set<string>;
  onToggleFolder: (folderId: string) => void;
  isManageMode: boolean;
  canManageMacros: boolean;
  canEditPlatformMacros: boolean;
  currentUserId: string | undefined;
  deletingFolderId: string | null;
  deleteFolderLoading: boolean;
  onDeleteFolder: (folder: RecordFolder) => void;
  editingFolderId: string | null;
  editingFolderName: string;
  updateFolderLoading: boolean;
  onStartRenameFolder: (folder: RecordFolder) => void;
  onCancelRenameFolder: () => void;
  onRenameFolder: (folder: RecordFolder) => void;
  onEditFolderNameChange: (value: string) => void;
  deletingMacroId: string | null;
  favoriteMacroIds: Set<string>;
  isMacroFavoritable: (macro: Macro) => boolean;
  onToggleFavoriteMacro: (macro: Macro) => void;
  onSelectMacro: (macro: Macro) => void | Promise<void>;
  onDeleteMacro: (macro: Macro) => void | Promise<void>;
  onListKeyDown: (event: KeyboardEvent<HTMLUListElement>) => void;
  highlightedIndex: number;
  activeEntryId: string | null;
  visibleEntryIndexById: Map<string, number>;
  listItemRefs: MutableRefObject<(HTMLDivElement | null)[]>;
}

export const MacroListContent = ({
  isCreatingFolder,
  newFolderName,
  onNewFolderNameChange,
  onCreateFolder,
  onCancelCreateFolder,
  createFolderLoading,
  isLoading,
  isSearching,
  hasResults,
  groupedFolders,
  expandedFolderIds,
  onToggleFolder,
  isManageMode,
  canManageMacros,
  canEditPlatformMacros,
  currentUserId,
  deletingFolderId,
  deleteFolderLoading,
  onDeleteFolder,
  editingFolderId,
  editingFolderName,
  updateFolderLoading,
  onStartRenameFolder,
  onCancelRenameFolder,
  onRenameFolder,
  onEditFolderNameChange,
  deletingMacroId,
  favoriteMacroIds,
  isMacroFavoritable,
  onToggleFavoriteMacro,
  onSelectMacro,
  onDeleteMacro,
  onListKeyDown,
  highlightedIndex,
  activeEntryId,
  visibleEntryIndexById,
  listItemRefs,
}: MacroListContentProps) => {
  let content: JSX.Element;

  if (isLoading) {
    content = <MacroLoadingState />;
  } else if (!hasResults) {
    content = <MacroEmptyState isSearching={isSearching} />;
  } else {
    content = (
      <MacroFolderList
        groupedFolders={groupedFolders}
        expandedFolderIds={expandedFolderIds}
        onToggleFolder={onToggleFolder}
        isManageMode={isManageMode}
        canManageMacros={canManageMacros}
        canEditPlatformMacros={canEditPlatformMacros}
        currentUserId={currentUserId}
        deletingFolderId={deletingFolderId}
        deleteFolderLoading={deleteFolderLoading}
        onDeleteFolder={onDeleteFolder}
        editingFolderId={editingFolderId}
        editingFolderName={editingFolderName}
        updateFolderLoading={updateFolderLoading}
        onStartRenameFolder={onStartRenameFolder}
        onCancelRenameFolder={onCancelRenameFolder}
        onRenameFolder={onRenameFolder}
        onEditFolderNameChange={onEditFolderNameChange}
        deletingMacroId={deletingMacroId}
        favoriteMacroIds={favoriteMacroIds}
        isMacroFavoritable={isMacroFavoritable}
        onToggleFavoriteMacro={onToggleFavoriteMacro}
        onSelectMacro={onSelectMacro}
        onDeleteMacro={onDeleteMacro}
        onListKeyDown={onListKeyDown}
        highlightedIndex={highlightedIndex}
        activeEntryId={activeEntryId}
        visibleEntryIndexById={visibleEntryIndexById}
        listItemRefs={listItemRefs}
      />
    );
  }

  return (
    <Stack flex={1} overflow="hidden">
      {!!isCreatingFolder && (
        <MacroCreateFolderRow
          name={newFolderName}
          isLoading={createFolderLoading}
          onChangeName={onNewFolderNameChange}
          onCreate={onCreateFolder}
          onCancel={onCancelCreateFolder}
        />
      )}
      {content}
    </Stack>
  );
};
